import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchAllUsers } from "../../../redux/authSlice";
import { listenToHostReservations } from "../../../redux/shortstayReservationSlice";
import { listenToShortstayData } from "../../../redux/shortstaysSlice";

const TodayShort: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const host   = useSelector((state: RootState) => state.auth.user);
  const hostId = host?.userId || host?.uid;

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.hostReservations
  );
  const users    = useSelector((state: RootState) => state.auth.allUsers);
  const listings = useSelector((state: RootState) => state.shortstays.listings);

  const [fetchedListings, setFetchedListings] = useState<string[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedStatus,  setSelectedStatus]  = useState("currentlyHosting");

  useEffect(() => {
    if (hostId) {
      dispatch(listenToHostReservations(hostId));
      dispatch(fetchAllUsers());
    }
  }, [dispatch, hostId]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToShortstayData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
    setLoading(false);
  }, [reservations, fetchedListings, dispatch]);

  const groupedReservations = useMemo(() => {
    const grouped: { [key: string]: typeof reservations } = {
      confirmed:        [],
      currentlyHosting: [],
      arrivingSoon:     [],
      upcoming:         [],
      checkingOut:      [],
      completed:        [],
      cancelled:        [],
    };

    reservations.forEach((res) => {
      if (res.isCancelled)                        grouped.cancelled.push(res);
      else if (res.isCheckingIn || res.isOngoing) grouped.currentlyHosting.push(res);
      else if (res.isCheckingOut)                 grouped.checkingOut.push(res);
      else if (res.isArrivingSoon)                grouped.arrivingSoon.push(res);
      else if (res.isUpcoming)                    grouped.upcoming.push(res);
      else if (res.isCompleted)                   grouped.completed.push(res);
      else if (res.isConfirmed)                   grouped.confirmed.push(res);
    });

    return grouped;
  }, [reservations]);

  const filteredReservations = groupedReservations[selectedStatus] || [];

  /* navigate using @username always */
  const handleGuestClick = (guest: any, guestId: string) => {
    const target = guest?.username;
    navigate(`/aboutme/@${target}`);
  };

  const handleListingClick = (listingId: string) => {
    navigate(`/roomdetails/${listingId}`);
  };

  const handleReviewClick = (res: any, guest: any) => {
    navigate(`/hostreview/${hostId}`, {
      state: { reservation: res, guest },
    });
  };

  /* status label prettifier */
  const labelMap: Record<string, string> = {
    confirmed:        "Confirmed",
    currentlyHosting: "Hosting",
    arrivingSoon:     "Arriving",
    upcoming:         "Upcoming",
    checkingOut:      "Checking out",
    completed:        "Completed",
    cancelled:        "Cancelled",
  };

  /* status accent colours (pill bg when active) */
  const accentMap: Record<string, string> = {
    confirmed:        "#1565C0",
    currentlyHosting: "#D32F2F",
    arrivingSoon:     "#E64A19",
    upcoming:         "#F57C00",
    checkingOut:      "#6A1B9A",
    completed:        "#2E7D32",
    cancelled:        "#757575",
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600&display=swap');

        .today-filter-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 0 0 12px 0;
          scrollbar-width: none;
        }
        .today-filter-scroll::-webkit-scrollbar { display: none; }

        .today-filter-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          border-radius: 20px;
          border: 1.5px solid #E8E8E8;
          background: #FFFFFF;
          cursor: pointer;
          flex-shrink: 0;
          font-family: 'Montserrat', sans-serif;
          font-size: 11px;
          font-weight: 500;
          color: #555;
          letter-spacing: 0.02em;
          transition: all .18s ease;
        }
        .today-filter-btn:hover {
          border-color: #CCCCCC;
          background: #FFF5F5;
          color: #D32F2F;
        }
        .today-filter-btn.active {
          background: var(--btn-accent);
          border-color: var(--btn-accent);
          color: #fff;
        }
        .today-filter-btn .count-badge {
          background: rgba(255,255,255,0.25);
          border-radius: 10px;
          padding: 1px 7px;
          font-size: 10px;
          font-weight: 600;
        }
        .today-filter-btn:not(.active) .count-badge {
          background: #F1F1F1;
          color: #888;
        }

        /* reservation card */
        .res-card {
          background: #FFFFFF;
          border: 1px solid #EBEBEB;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          margin-bottom: 10px;
          max-width: 640px;
          transition: box-shadow .2s, transform .2s;
          flex-wrap: wrap;
        }
        .res-card:hover {
          box-shadow: 0 4px 20px rgba(211,47,47,0.08);
          transform: translateY(-1px);
        }

        /* 3-D initials avatar */
        .avatar-3d {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 16px;
          color: #fff;
          cursor: pointer;
          flex-shrink: 0;
          position: relative;
          /* logo gradient + 3-D depth via layered shadows */
          background: linear-gradient(145deg, #E64A19 0%, #D32F2F 60%, #9B1B1B 100%);
          box-shadow:
            0 1px 0  1px rgba(255,255,255,0.35) inset,
            0 -1px 0 1px rgba(0,0,0,0.25) inset,
            0 4px 8px rgba(211,47,47,0.45),
            0 8px 16px rgba(211,47,47,0.2);
          letter-spacing: 0.04em;
          text-shadow: 0 1px 2px rgba(0,0,0,0.4);
          transition: box-shadow .2s, transform .2s;
          border: 2px solid rgba(255,255,255,0.6);
        }
        .avatar-3d:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow:
            0 1px 0  1px rgba(255,255,255,0.35) inset,
            0 -1px 0 1px rgba(0,0,0,0.25) inset,
            0 6px 14px rgba(211,47,47,0.55),
            0 12px 24px rgba(211,47,47,0.25);
        }

        /* profile photo — 3-D ring */
        .avatar-photo {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          cursor: pointer;
          flex-shrink: 0;
          border: 2px solid rgba(255,255,255,0.9);
          box-shadow:
            0 4px 8px rgba(211,47,47,0.4),
            0 8px 16px rgba(211,47,47,0.15);
          transition: box-shadow .2s, transform .2s;
        }
        .avatar-photo:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow:
            0 6px 14px rgba(211,47,47,0.5),
            0 12px 24px rgba(211,47,47,0.2);
        }

        /* listing thumbnail */
        .listing-thumb {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          object-fit: cover;
          cursor: pointer;
          flex-shrink: 0;
          border: 1px solid #E8E8E8;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: box-shadow .2s, transform .2s;
        }
        .listing-thumb:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        }

        /* arrow between avatars */
        .flow-arrow {
          color: #CCCCCC;
          font-size: 18px;
          flex-shrink: 0;
        }

        /* date + meta block */
        .res-meta {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          min-width: 0;
        }
        .res-guest-name {
          font-size: 13px;
          font-weight: 600;
          color: #111;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
        }
        .res-guest-name:hover { color: #D32F2F; }
        .res-dates {
          font-size: 12px;
          color: #888;
        }

        /* review button */
        .review-btn {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Montserrat', sans-serif;
          border: 1.5px solid;
          transition: all .2s ease;
          flex-shrink: 0;
          cursor: pointer;
        }
        .review-btn.enabled {
          background: #D32F2F;
          border-color: #D32F2F;
          color: #fff;
        }
        .review-btn.enabled:hover {
          background: #B71C1C;
          border-color: #B71C1C;
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(211,47,47,0.3);
        }
        .review-btn.disabled {
          background: #F5F5F5;
          border-color: #E0E0E0;
          color: #BDBDBD;
          cursor: not-allowed;
        }

        .empty-state {
          padding: 40px 0;
          color: #AAAAAA;
          font-size: 13px;
          font-family: 'Montserrat', sans-serif;
        }
      `}</style>

      {/* ── FILTER PILLS ── */}
      <div className="today-filter-scroll">
        {Object.keys(groupedReservations).map((status) => {
          const isActive = selectedStatus === status;
          const accent   = accentMap[status] || "#D32F2F";
          return (
            <button
              key={status}
              className={`today-filter-btn${isActive ? " active" : ""}`}
              style={{ "--btn-accent": accent } as React.CSSProperties}
              onClick={() => setSelectedStatus(status)}
            >
              {labelMap[status] || status}
              <span className="count-badge">
                {groupedReservations[status].length}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── CARDS ── */}
      {loading ? (
        <p className="empty-state">Loading reservations…</p>
      ) : filteredReservations.length === 0 ? (
        <p className="empty-state">No reservations in "{labelMap[selectedStatus] || selectedStatus}".</p>
      ) : (
        filteredReservations.map((res) => {
          const guest      = users.find((u: any) => u.userId === res.guestId);
          const listing    = listings.find((l: any) => l.listingId === res.listingId);
          const listingImg = listing?.photos?.[0] || "/fallback.jpg";
          const canReview  = !!res.isCheckingOut;

          const firstName   = guest?.firstName || "";
          const lastName    = guest?.lastName  || "";
          const fullName    = [firstName, lastName].filter(Boolean).join(" ") || "Guest";
          const initials    = `${firstName[0] || "G"}${lastName[0] || ""}`.toUpperCase();

          return (
            <div key={res.id} className="res-card">

              {/* ── guest avatar — 3-D sphere or photo ── */}
              {guest?.profilePicture ? (
                <img
                  src={guest.profilePicture}
                  className="avatar-photo"
                  alt={fullName}
                  onClick={() => handleGuestClick(guest, res.guestId)}
                />
              ) : (
                <div
                  className="avatar-3d"
                  onClick={() => handleGuestClick(guest, res.guestId)}
                  title={fullName}
                >
                  {initials}
                </div>
              )}

              <span className="flow-arrow">→</span>

              {/* ── listing thumbnail ── */}
              <img
                src={listingImg}
                className="listing-thumb"
                alt="Listing"
                onClick={() => handleListingClick(res.listingId)}
              />

              {/* ── meta block ── */}
              <div className="res-meta">
                <span
                  className="res-guest-name"
                  onClick={() => handleGuestClick(guest, res.guestId)}
                >
                  {fullName}
                </span>
                <span className="res-dates">
                  {new Date(res.checkIn).toLocaleDateString()} → {new Date(res.checkOut).toLocaleDateString()}
                </span>
              </div>

              {/* ── review button ── */}
              <button
                className={`review-btn ${canReview ? "enabled" : "disabled"}`}
                onClick={() => canReview && handleReviewClick(res, guest)}
                disabled={!canReview}
              >
                Review
              </button>

            </div>
          );
        })
      )}
    </>
  );
};

export default TodayShort;